import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Brush } from 'recharts';
import { format, parseISO, isValid } from 'date-fns';

interface DailyNervGraphProps {
  data: any[];
  showComparison?: boolean;
  viewMode?: 'current' | 'realistic' | 'potential' | 'combined';
  organisation?: any;
}

// Color palette for up to 10 SKUs
const COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
  'hsl(142, 76%, 36%)',
  'hsl(280, 65%, 60%)',
  'hsl(45, 93%, 47%)',
  'hsl(340, 75%, 55%)',
  'hsl(200, 80%, 50%)',
];

const DailyNervGraph: React.FC<DailyNervGraphProps> = ({ data, showComparison = false, viewMode = 'current', organisation }) => {
  const drPercent = Number(organisation?.dr_sku_percentage ?? 0) || 0;

  // Custom tooltip for aggregated data with calculation breakdown
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || payload.length === 0) return null;

    const dataPoint = payload[0].payload;
    // Units used is the MIN(total_inventory, total_sold) calculated at aggregate level
    const unitsUsed = dataPoint.unitsUsed ?? Math.min(dataPoint.totalUnitsSold || 0, dataPoint.totalInventory || 0);

    return (
      <div className="bg-popover border border-border rounded-lg p-3 shadow-lg min-w-[200px]">
        <p className="font-semibold text-popover-foreground mb-2">{formatTooltipLabel(label)}</p>

        <div className="space-y-1 text-sm">
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">NERV:</span>
            <span className="font-semibold text-popover-foreground">{formatCurrency(dataPoint.current)}</span>
          </div>

          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Units Used:</span>
            <span className="text-popover-foreground">{(unitsUsed || 0).toLocaleString()}</span>
          </div>


          <div className="border-t border-border pt-2 mt-2">
            <p className="text-xs text-muted-foreground mb-1.5 font-medium">Totals Across All SKUs:</p>

            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Units Sold:</span>
              <span className="text-popover-foreground">{(dataPoint.totalUnitsSold || 0).toLocaleString()}</span>
            </div>

            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Inventory:</span>
              <span className="text-popover-foreground">{(dataPoint.totalInventory || 0).toLocaleString()}</span>
            </div>

            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">DR %:</span>
              <span className="text-popover-foreground">{drPercent}%</span>
            </div>
          </div>

          <div className="border-t border-border pt-2 mt-1 space-y-1">
            <p className="text-xs text-muted-foreground italic">
              For each SKU: NERV = MIN(Stock, Sold) × Avg Price × (1 - DR%)
            </p>
            <p className="text-xs text-muted-foreground">
              Total NERV = Sum of all per-SKU NERVs
            </p>
          </div>
        </div>
      </div>
    );
  };
  // Check if data is aggregated (dataset comparison) or per-SKU
  const isAggregatedData = useMemo(() => {
    return data.length > 0 && ('current' in data[0] || 'uploadedRealistic' in data[0]);
  }, [data]);

  // Transform data from [{date, sku, nerv, inventory_level, avg_price}] to [{date, sku1: nerv, sku2: nerv, ...}]
  const graphData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // If already aggregated, use as-is
    if (isAggregatedData) {
      return data;
    }

    // Otherwise, aggregate by SKU
    const dateMap = new Map<string, any>();

    data.forEach((row) => {
      const dateKey = row.date;
      const sku = row.sku;
      const nerv = Number(row.nerv ?? 0);

      if (!dateMap.has(dateKey)) {
        dateMap.set(dateKey, { date: dateKey });
      }

      const dateEntry = dateMap.get(dateKey);
      dateEntry[sku] = nerv;
    });

    return Array.from(dateMap.values()).sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA.getTime() - dateB.getTime();
    });
  }, [data, isAggregatedData]);

  // Get unique SKUs from data (only for per-SKU mode)
  const skus = useMemo(() => {
    if (!data || data.length === 0 || isAggregatedData) return [];
    const uniqueSkus = new Set<string>();
    data.forEach((row) => {
      const sku = row.sku;
      if (sku) uniqueSkus.add(sku);
    });
    return Array.from(uniqueSkus).slice(0, 10); // Max 10 SKUs
  }, [data, isAggregatedData]);

  const formatXAxis = (dateStr: string) => {
    try {
      const dateObj = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
      if (isValid(dateObj)) {
        return format(dateObj, 'MMM d');
      }
    } catch (e) {
      // fallback
    }
    return dateStr;
  };

  const formatTooltipLabel = (dateStr: string) => {
    try {
      const dateObj = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
      if (isValid(dateObj)) {
        return format(dateObj, 'MMM d, yyyy');
      }
    } catch (e) {
      // fallback
    }
    return dateStr;
  };

  const formatCurrency = (value: any) => {
    let num = Number(value);
    if (isNaN(num)) return '$0.00'
    num = Number(num.toFixed(2))


    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (graphData.length === 0) {
    return (
      <div className="flex items-center justify-center h-[400px] text-muted-foreground">
        {`No data available to display.${!isAggregatedData && skus.length === 0 ? ' Please select a SKU to view the graph.' : ''}`}
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={400}>
      <LineChart data={graphData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
        <XAxis
          dataKey="date"
          tickFormatter={formatXAxis}
          stroke="hsl(var(--muted-foreground))"
          style={{ fontSize: '12px' }}
        />
        <YAxis
          stroke="hsl(var(--muted-foreground))"
          style={{ fontSize: '12px' }}
          allowDuplicatedCategory={false}
          tickFormatter={(value) => {
            const abs = Math.abs(value);
            if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
            if (abs >= 1_000) return `$${(value / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`;
            return `$${value.toFixed(0)}`;
          }}
        />
        {isAggregatedData ? (
          <Tooltip content={<CustomTooltip />} />
        ) : (
          <Tooltip
            labelFormatter={formatTooltipLabel}
            contentStyle={{
              backgroundColor: 'hsl(var(--popover))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '6px',
              color: 'hsl(var(--popover-foreground))',
            }}
            formatter={(value: any, name: string, props: any) => {
              // For per-SKU data, show detailed info
              const row = data.find((d) => d.date === props.payload.date && d.sku === name);
              if (row) {
                return [
                  <div key="tooltip" className="space-y-1">
                    <div className="font-semibold">{formatCurrency(value)}</div>
                    <div className="text-xs text-muted-foreground">
                      Inventory: {Number(row.inventory_level)?.toLocaleString('en-US') ?? 0} units
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Avg Price: {formatCurrency(row.avg_price ?? 0)}
                    </div>
                  </div>,
                  name
                ];
              }
              return [formatCurrency(value), name];
            }}
          />
        )}
        <Legend
          wrapperStyle={{ fontSize: '12px' }}
          formatter={(value) => {
            // Truncate long SKU names in legend
            if (value.length > 25) return value.substring(0, 25) + '...';
            return value;
          }}
        />
        {isAggregatedData ? (
          // Render dataset comparison lines
          <>
            {(viewMode === 'current' || viewMode === 'combined') && (
              <Line
                key="current"
                type="monotone"
                dataKey="current"
                stroke={COLORS[0]}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                name="Current NERV"
                connectNulls
              />
            )}
            {viewMode === 'realistic' && (
              <Line
                key="combinedRealistic"
                type="monotone"
                dataKey="combinedRealistic"
                stroke={COLORS[1]}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                name="Realistic NERV (current + uploaded capped)"
                connectNulls
              />
            )}
            {viewMode === 'potential' && (
              <Line
                key="combinedPotential"
                type="monotone"
                dataKey="combinedPotential"
                stroke={COLORS[5]}
                strokeWidth={2}
                strokeDasharray="3 3"
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                name="Potential NERV (current + uploaded full)"
                connectNulls
              />
            )}
            {viewMode === 'combined' && (
              <>
                <Line
                  key="combinedRealistic"
                  type="monotone"
                  dataKey="combinedRealistic"
                  stroke={COLORS[1]}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  name="Realistic (current + capped)"
                  connectNulls
                />
                <Line
                  key="combinedPotential"
                  type="monotone"
                  dataKey="combinedPotential"
                  stroke={COLORS[5]}
                  strokeWidth={2}
                  strokeDasharray="3 3"
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  name="Potential (current + full)"
                  connectNulls
                />
              </>
            )}
          </>
        ) : (
          // Render per-SKU lines
          skus.map((sku, index) => (
            <Line
              key={sku}
              type="monotone"
              dataKey={sku}
              stroke={COLORS[index % COLORS.length]}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              name={sku}
              connectNulls
            />
          ))
        )}
        <Brush
          dataKey="date"
          height={30}
          stroke="hsl(var(--primary))"
          fill="hsl(var(--muted))"
          tickFormatter={formatXAxis}
          travellerWidth={10}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default DailyNervGraph;
